import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { ApiResourceService } from 'libraries/service/apiResource/apiResource.service';
import { ISearchRequest, IOtherRoomsRequest, IQuoteRequest, ISegment } from './ISearchParameters';
import {
    ISearchOffersResponse,
    IHotelQuoteResponse,
    IGetPropertyResponse,
    ICompleteQuote,
    IQuotes
} from './ISearchResponse';
import { ICreateBookingResult } from 'libraries/service/resources/model/search/IBooking';
import { IHotelQuoteResourceResponse } from "./hotel-quotation";
import { IQuoteRequestForResource } from "libraries/hotel/models/quote";
import { IPostHotelBookingsCreateRequest, IPostHotelBookingsCreateResponse } from './IPostHotelBookingsCreate';
import { HotelApiHeaderParameters } from "libraries/service/resources/hotel/hotel-api-header-parameters";
import { wenji } from "libraries/interfaces/IWenji";
import { IHeader } from "libraries/service/resource/IHeader";
import _cloneDeep from "lodash-es/cloneDeep";
import { UrlHelperService } from "libraries/service/url-helper/url-helper.service";
import {
    IBookingPropertyDescription,
    IGetBookingsStayResponse, IOriginalCurrency, IPrice, IPriceDetail,
    IProperty, IRule, IRulePenalty,
    TGetBookingResponse
} from "libraries/service/resources/booking/interfaces/IGetBookings";
import { BookingType } from "libraries/service/resources/booking/interfaces/get-bookings.enum";
import { map, tap } from "rxjs/operators";
import { Yii } from "libraries/interfaces/Yii";
import { PermissionResourceService } from "libraries/service/resources/permission/permissionResource.service";
import * as moment from "moment";
import { EStayPermissions } from "libraries/enums/e-stay-permissions";
import { LanguageService } from "libraries/service/language/language.service";

const BOOKINGS: string = 'bookings';
const CANCEL: string = 'cancel';
const CREATE: string = 'create';
const HOSTS: string = 'hosts';
const HOTEL: string = 'hotel';
const PROPERTIES: string = 'properties';
const QUOTE: string = 'quote';
const QUOTES: string = 'quotes';
const ROOMS: string = 'rooms';
const SEARCH: string = 'search';
const TRIPS: string = 'trips';


@Injectable()
export class HotelResourceService {
    private readonly headerParameters: IHeader = HotelApiHeaderParameters.headerParameters;

    private getQuotesCache: Map<string, IHotelQuoteResponse> = new Map<string, IHotelQuoteResponse>();
    constructor(
        private readonly apiResource: ApiResourceService,
        private readonly urlHelperService: UrlHelperService,
        private readonly permissionResourceService: PermissionResourceService,
        private readonly languageService: LanguageService,
    ) {
    }

    public loadSearchAndApplyPermissions(id: string): Observable<ISearchOffersResponse> {

        return forkJoin([
            this.loadSearch(id),
            this.permissionResourceService.hasPermission(EStayPermissions.SHARED_STAY_QUOTE_WITH_FEES_VIEW),
            this.permissionResourceService.hasPermission(EStayPermissions.SHARED_STAY_QUOTE_WITH_FEES_CHOOSE),
            /*
            for permission testing
            of(true),
            of(false),
            */
        ])
            .pipe(
                map(([response, canViewOfferWithFees, canChooseOfferWithFees]: [ISearchOffersResponse, boolean, boolean]): ISearchOffersResponse => {
                    return this.applyPermissionOnSearchResponse(response, canViewOfferWithFees, canChooseOfferWithFees);
                }),
            );
    }

    public searchAndApplyPermissions(parameters: ISearchRequest): Observable<ISearchOffersResponse> {


        return forkJoin([
            this.search(parameters),
            this.permissionResourceService.hasPermission(EStayPermissions.SHARED_STAY_QUOTE_WITH_FEES_VIEW),
            this.permissionResourceService.hasPermission(EStayPermissions.SHARED_STAY_QUOTE_WITH_FEES_CHOOSE),
            /*
            for permission testing
            of(true),
            of(false),
            */
        ])
            .pipe(
                map(([response, canViewOfferWithFees, canChooseOfferWithFees]: [ISearchOffersResponse, boolean, boolean]): ISearchOffersResponse => {
                    return this.applyPermissionOnSearchResponse(response, canViewOfferWithFees, canChooseOfferWithFees);
                }),
            );
    }

    public search(parameters: ISearchRequest): Observable<ISearchOffersResponse> {
        const request: ISearchRequest = this.computeLocationCorrections(parameters);


        return this.apiResource.postOne<ISearchRequest, ISearchOffersResponse>(
            this.apiResource.getApiUrl(HOTEL, TRIPS, SEARCH),
            request,
            undefined,
            {
                target: wenji.ui.target,
            }
        );
    }

    public loadSearch(id: string): Observable<ISearchOffersResponse> {
        return this.apiResource.getOne<ISearchOffersResponse>(
            this.apiResource.getApiUrl(HOTEL, TRIPS, id),
            undefined,
            {
                target: wenji.ui.target,
            }
        );
    }

    public getQuotes(request: IQuoteRequest): Observable<IHotelQuoteResponse> {
        //https://projets2.afidium.com/issues/33879
        // added cache
        const url: string = this.apiResource
            .getApiUrl(
                HOTEL, TRIPS, request.trips, PROPERTIES, request.properties, QUOTES
            );

        if(this.getQuotesCache.has(url)){
            return of(this.getQuotesCache.get(url))
        }

        return this.apiResource.getOne<IHotelQuoteResponse>(
            url,
            {},
            this.computeHeaderParameters(),
        )
            .pipe(
                tap((response:IHotelQuoteResponse) => {
                    this.getQuotesCache.set(url, response)
                })
            );
    }

    public quote(
        request: IQuoteRequest,
        parameters: IQuoteRequestForResource | IQuoteRequest
    ): Observable<IHotelQuoteResourceResponse> {
        return this.apiResource.postOne(
            this.apiResource.getApiUrl(
                HOTEL, TRIPS, request.trips, PROPERTIES, request.properties, HOSTS, request.hosts, QUOTE),
            parameters,
            undefined,
            {
                target: wenji.ui.target,
            }
        );
    }

    public book(request: any): Observable<IPostHotelBookingsCreateResponse> {
        return this.apiResource.postOne<IPostHotelBookingsCreateRequest, IPostHotelBookingsCreateResponse>(
            this.apiResource.getApiUrl(HOTEL, BOOKINGS, CREATE),
            request,
            undefined,
            {
                target: wenji.ui.target,
            }
        );
    }


    public getQuote(idQuotation: string): Observable<any> {
        const url = this.apiResource.getApiUrl(HOTEL, QUOTES, idQuotation);
        return this.apiResource.getOne(url, undefined, {target: wenji.ui.target});
    }

    public getRoomsByHost(parameters: IOtherRoomsRequest): Observable<ISearchOffersResponse> {
        return this.apiResource.getOne<ISearchOffersResponse>(
            this.apiResource
                .getApiUrl(
                    HOTEL,
                    TRIPS,
                    parameters.trips,
                    PROPERTIES,
                    parameters.properties,
                    HOSTS,
                    parameters.hosts,
                    ROOMS
                ),
            {},
            this.computeHeaderParameters(),
        );
    }

    public getBookingById(key: string, options?: object): Observable<any> {
        return this.apiResource.getOne<TGetBookingResponse>(
            this.apiResource.getApiUrl(
                HOTEL,
                BOOKINGS,
                this.urlHelperService.retrieveCharacters(key)
            ),
            options,
            this.computeHeaderParameters(),
        ).pipe(
            tap(this.sanitizeBooking.bind(this)),
            tap((response: TGetBookingResponse) => {
                // #32815 : in case of uncatch error in xsl, we have data : [] in resposne.
                // we catch it here.
                if (!response) {
                    throw new Error(Yii.t('wui-shared-js', 'Erreur lors de la récupération du dossier.'));
                }
            }),
        );
    }

    public getQuotationById(key: string): Observable<IGetPropertyResponse> {
        return this.apiResource.getOne<IGetPropertyResponse>(
            this.apiResource.getApiUrl(HOTEL, QUOTES, key),
            {},
            this.computeHeaderParameters(),
        );
    }

    public cancelBooking(idBooking: number | string, host: string): Observable<ICreateBookingResult> {
        return this.apiResource.postOne(
            this.apiResource.getApiUrl(HOTEL, BOOKINGS, idBooking, CANCEL),
            {},
            {hosts: host},
            {target: wenji.ui.target}
        );
    }

    /*
    https://redmine.afidium.com/issues/30863#note-7
    always remove location if geoposition is given
    if no geolocation, no location, and locationName, copy locationName in location
    do it on deepCopy to avoid side effects
     */
    private computeLocationCorrections(parameters: ISearchRequest): ISearchRequest {
        const request: ISearchRequest = _cloneDeep(parameters);
        if (request?.segment?.length) {
            request.segment = request.segment.map((segment: ISegment): ISegment => {
                if (segment.property?.geoposition && (!segment.property?.geoposition.longitude || !segment.property?.geoposition.latitude)) {
                    delete segment.property.geoposition;
                }
                const hasGeoposition: boolean = Boolean(segment.property?.geoposition);
                const hasLocation: boolean = Boolean(segment.property?.location);
                const hasLocationName: boolean = Boolean(segment.property?.locationName);
                const isObjectLocation: boolean = Boolean(hasLocation && typeof segment.property.location !== "string");

                if (isObjectLocation) {
                    // @ts-ignore
                    segment.property.location = segment.property.location.name;
                }
                if (hasGeoposition && hasLocation) {
                    const segmentCopy: ISegment = _cloneDeep(segment);
                    delete segmentCopy.property.location;
                    return segmentCopy;
                } else if (!hasGeoposition && !hasLocation && hasLocationName) {
                    const segmentCopy: ISegment = _cloneDeep(segment);
                    // @ts-ignore
                    segmentCopy.property.location = segmentCopy.property.locationName;
                    return segmentCopy;
                }
                return segment;
            });
        }

        return request;
    }

    private sanitizeBooking(booking: TGetBookingResponse): void {
        // For compatibility reasons, we can have bookings with properties that are not arrays
        // as we cannot synchronise hotel bookings for the moment

        if (booking?.type && booking.type.some((bookingType: BookingType) => bookingType === BookingType.stay)) {
            const property: IProperty[] | IProperty = (booking as IGetBookingsStayResponse).property;
            if (property && !Array.isArray(property)) {
                (booking as IGetBookingsStayResponse).property = [property];
            }
            (booking as IGetBookingsStayResponse).price = parseStayPrice((booking as IGetBookingsStayResponse).price);
            (booking as IGetBookingsStayResponse).property = parseStayProperty((booking as IGetBookingsStayResponse).property);
            (booking as IGetBookingsStayResponse).rule = parseRuleProperty((booking as IGetBookingsStayResponse).rule);
        }
    }

    private applyPermissionOnSearchResponse(response: ISearchOffersResponse, canViewOfferWithFees: boolean, canChooseOfferWithFees: boolean): ISearchOffersResponse {
        if (
            (canViewOfferWithFees && canChooseOfferWithFees) ||
            (!canViewOfferWithFees && canChooseOfferWithFees)
        ) {
            return response;
        }
        if (!canViewOfferWithFees) {
            // @ts-ignore
            const quotesWithNoFees: IQuotes = {};
            Object.entries(response.quote)
                .forEach(([key, quote]: [string, ICompleteQuote]): void => {
                    const quoteDate: Date = new Date((quote.dateLastCancellationWithoutPenalty ? quote.dateLastCancellationWithoutPenalty.split('+')[0] : '') || '');
                    const isQuoteBefore: boolean = moment(quoteDate).isSameOrBefore(moment());
                    if ((!quote.dateLastCancellationWithoutPenalty) || !isQuoteBefore) {
                        quotesWithNoFees[key] = quote;
                    }
                });
            return {
                ...response,
                quote: quotesWithNoFees,
            };
        }
        if (canViewOfferWithFees && !canChooseOfferWithFees) {
            // @ts-ignore
            const quotesWithNoSelectionInformation: IQuotes = {};
            Object.entries(response.quote)
                .forEach(([key, quote]: [string, ICompleteQuote]): void => {
                    // const quoteDate: Date = new Date((quote.dateLastCancellationWithoutPenalty) || '');
                    const quoteDate: Date = new Date((quote.dateLastCancellationWithoutPenalty ? quote.dateLastCancellationWithoutPenalty.split('+')[0] : '') || '');
                    const isQuoteBefore: boolean = moment(quoteDate).isSameOrBefore(moment());
                    if (isQuoteBefore) {
                        quotesWithNoSelectionInformation[key] = {
                            ...quote,
                            isNotSelectable: true,
                        };
                    } else {
                        quotesWithNoSelectionInformation[key] = quote;
                    }
                });
            return {
                ...response,
                quote: quotesWithNoSelectionInformation,
            };
        }
        return response;

    }

    private computeHeaderParameters(): IHeader {
        return {
            ...this.headerParameters,
            language: this.languageService.userLanguage,
        };
    }
}

function parseRuleProperty(ruleProperty: IRule[]): IRule[] {
    return (ruleProperty || []).map(
        (property: IRule) => {
            if (property && property.penalty) {
                const penalty: IRulePenalty[] = property.penalty
                    .map((penaltyItem: IRulePenalty) => {
                        // @ts-ignore
                        if (penaltyItem.validity && penaltyItem.validity.validity) {
                            // @ts-ignore
                            penaltyItem.validity.cancellation = penaltyItem.validity.validity;
                            // @ts-ignore
                            delete penaltyItem.validity.validity;
                        }
                        return penaltyItem;
                    });

                return {
                    ...property,
                    penalty,
                };
            }
            return property;
        }
    );
}

function parseStayPrice(bookingPrice: IPrice[]): IPrice[] {
    return (bookingPrice || []).map(
        (priceItem: IPrice) => {
            if (!priceItem.detail || !(Object.keys(priceItem.detail).length > 0) || !priceItem.detail.base) {
                return priceItem;
            }

            const priceDetailBase: IPriceDetail[] = priceItem.detail.base.map(
                (basePrice: IPriceDetail) => ({
                    ...basePrice,
                    accomodation: basePrice.accomodation && !Array.isArray(basePrice.accomodation) ?
                        [basePrice.accomodation] : basePrice.accomodation,
                    traveller: basePrice.traveller && !Array.isArray(basePrice.traveller) ?
                        [basePrice.traveller] : basePrice.traveller,
                })
            );

            // @ts-ignore
            if (priceItem.detail.supplement && priceItem.detail.supplement.amount) {
                // @ts-ignore
                priceItem.detail.supplement = [priceItem.detail.supplement];
                // @ts-ignore
            } else if (
                priceItem.detail.supplement &&
                !Array.isArray(priceItem.detail.supplement) &&
                // @ts-ignore
                priceItem.detail.supplement.originalCurrency &&
                // @ts-ignore
                !priceItem.detail.supplement.amount
            ) {
                // @ts-ignore
                const originalCurrency: IOriginalCurrency = priceItem.detail.supplement.originalCurrency;
                priceItem.detail.supplement = [
                    {
                        // @ts-ignore
                        ...priceItem.detail.supplement,
                        ...originalCurrency
                    }
                ];
            }

            // @ts-ignore
            const priceDetailSupplement: IPriceDetail[] = priceItem.detail.supplement ? priceItem.detail.supplement.map(
                (price: IPriceDetail) => ({
                    ...price,
                    accomodation: price.accomodation && !Array.isArray(price.accomodation) ?
                        [price.accomodation] : price.accomodation,
                    traveller: price.traveller && !Array.isArray(price.traveller) ?
                        [price.traveller] : price.traveller,
                })
            ) : undefined;

            return {
                ...priceItem,
                detail: {
                    ...priceItem.detail,
                    base: priceDetailBase,
                    supplement: priceDetailSupplement,
                }
            };
        }
    );
}

function parseStayProperty(bookingProperty: IProperty[]): IProperty[] {
    return (bookingProperty || []).map(
        (property: IProperty) => {
            let description: IBookingPropertyDescription[] = property.description;
            if (description) {
                description = (Array.isArray(description) ? description : [description])
                    .map(
                        (descriptionItem: IBookingPropertyDescription): IBookingPropertyDescription => {
                            return descriptionItem;
                        }
                    )
                    .map((desc: IBookingPropertyDescription) => {
                        let output = desc;
                        // @ts-ignore
                        if (output.type) {
                            output = {
                                ...output,
                                // @ts-ignore
                                types: [output.type]
                            };
                        }
                        // @ts-ignore
                        if (output.textLine && !output.textLines?.length) {
                            output = {
                                ...output,
                                // @ts-ignore
                                textLines: Array.isArray(output.textLine) ? output.textLines : [output.textLine],
                            };
                        }
                        return output;
                    });
            }
            return {
                ...property,
                description
            };
        }
    );

}
